import { UserType } from '@a_team/models/dist/UserObject';
import {
  Breakpoints,
  Colors,
  FontWeights,
  Icon,
  IconType,
  Paddings,
  Spacing,
  TextColors,
} from '@ateams/components';
import LoadingIndicator from '@src/components/LoadingIndicator';
import CodeOfConductModal from '@src/components/Modal/CodeOfConductModal';
import ReSubscribeMissionNotificationsModal from '@src/components/Modal/ReSubscribeMissionNotificationsModal';
import ReleaseStepsModal from '@src/components/Modal/ReleaseStepsModalTeamUp';
import ReleaseTeamUpModal from '@src/components/Modal/ReleaseTeamUpModal';
import MakerToSModal from '@src/components/Modal/ToSModal';
import { ClientToSModal } from '@src/components/Modal/ToSModal/ClientToSModal';
import TokenIntroductionModal from '@src/components/Modal/TokenIntroductionModal';
import NotificationCenter from '@src/components/NotificationSlider';
import ReleaseConnectionsBanner from '@src/components/ReleaseConnectionsBanner';
import StickyStrip from '@src/components/StickyStrip';
import useClickOutside from '@src/hooks/useClickOutside';
import useLoadingState from '@src/hooks/useLoadingState';
import useToggle from '@src/hooks/useToggle';
import MobileTopBar from '@src/layouts/Main/MobileTopBar';
import {
  ClientTermsOfServiceLocation,
  PrivacyPolicyLocation,
  TalentPrivacyStatementLocation,
  TermsOfServiceLocation,
  TokensLocation,
} from '@src/locations';
import { useStores } from '@src/stores';
import cx from 'classnames';
import { observer } from 'mobx-react';
import React, {
  CSSProperties,
  ReactElement,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createUseStyles } from 'react-jss';
import { Link, useLocation } from 'react-router-dom';
import { LinkedinBanner } from './LinkedinBanner';
import Navbar from './Navbar';
import { useScreenClass } from 'react-grid-system';
import { AcceptNewTOS } from '@src/components/AcceptNewTOS';
import { CONTRACTS_V2_ENABLED } from '@src/config';

export interface MainLayoutProps {
  className?: string;
  containerClassName?: string;
  contentClassName?: string;
  childrenClassName?: string;
  bgColor?: string | null;
  title: string | undefined;
  children: ReactNode;
  hero?: ReactNode;
  sidebar?: ReactNode;
  topNav?: ReactNode;
  isTopNavSticky?: boolean;
  naked?: boolean;
  style?: CSSProperties;
  hideNavbar?: boolean;
}

const useStyles = createUseStyles<{
  isAuth: boolean;
}>({
  '@global': {
    body: {
      backgroundColor: '#6d00d7',
      color: '#fff',
    },
  },
  root: {
    paddingLeft: 0,
  },
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'stretch',
    minHeight: '100vh',
    paddingBottom: 48,
  },
  children: {
    backgroundColor: '#FFFFFF',
    flexGrow: 1,
    color: '#222',
    width: '100%',

    '&.naked': {
      background: 'none',
    },
  },
  content: {
    marginTop: 32,
    padding: '24px 24px 48px 24px',
  },
  topNavWrapper: {
    position: 'sticky',
    top: 0,
    zIndex: 99,
  },
  termsFooter: {
    height: 50,
    textAlign: 'center',
    padding: '15px 40px',
    fontSize: 12,
    color: TextColors.regularLight,
    '& .link': {
      color: TextColors.primaryLight,
      cursor: 'pointer',
      pointerEvents: 'auto',
    },
    marginTop: '-60px',
    position: 'relative',
    pointerEvents: 'none',
    '&.naked': {
      color: TextColors.primary,
    },
  },
  linkedinBannerMobile: {
    position: 'sticky',
    top: 0,
    zIndex: 100,
    width: '100%',
  },
  [`@media (min-width: ${Breakpoints.sm}px)`]: {
    root: {
      paddingLeft: 120,
    },
    content: {
      marginTop: 0,
    },
    children: {
      borderLeft: '1px solid #DADADC',
    },
    container: {
      flexWrap: 'nowrap',
      paddingBottom: 0,
    },
  },
});

function MainLayout(props: MainLayoutProps): ReactElement {
  const {
    title,
    className,
    containerClassName,
    contentClassName,
    childrenClassName,
    children,
    bgColor,
    hero,
    sidebar,
    topNav,
    isTopNavSticky,
    naked,
    style,
    hideNavbar,
  } = props;

  const { document, auth, rewards, uiStore } = useStores();
  const screenClass = useScreenClass();
  const isMobile = screenClass === 'xs' || screenClass === 'sm';

  const styles = useStyles();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useToggle(false);
  const [TOSModal, setTOSModal] = useState(auth.user?.needsAcceptTOS === true);
  const [isCodeOfConductModalOpen, setIsCodeOfConductModalOpen] =
    useToggle(false);
  const [isWhatsNewModalOpen, setIsWhatsNewModalOpen] = useToggle(false);
  const [isReleaseStepsModalOpen, setIsReleaseStepsModalOpen] =
    useToggle(false);

  const [isReleaseBannerOpen, setIsReleaseBannerOpen] = useState(false);

  const [loading, setLoading] = useLoadingState();
  const menuRef = useRef(null);
  const hideTOSModal =
    location.pathname === ClientTermsOfServiceLocation ||
    location.pathname === PrivacyPolicyLocation ||
    location.pathname === TermsOfServiceLocation ||
    location.pathname === TalentPrivacyStatementLocation;

  const showTokenModal = location.pathname === TokensLocation;

  useClickOutside(menuRef, (): void => {
    if (!menuOpen) return;
    setMenuOpen(false);
  });

  useEffect((): (() => void) => {
    const { style } = window.document.body;
    let finalBgColor = '';
    if (bgColor) {
      finalBgColor = bgColor;
    } else if (auth.uid) {
      finalBgColor = '#FFFFFF';
    }

    style.backgroundColor = finalBgColor;

    return (): void => {
      style.backgroundColor = '';
    };
  }, [auth.uid, bgColor]);

  useEffect(() => {
    if (auth.user?.needsAcceptTOS === true && !CONTRACTS_V2_ENABLED) {
      setTOSModal(true);
    }
  }, [auth.user]);

  useEffect(() => {
    if (
      !auth.isActiveWaitlistedAdVerified && // If the user is verified and ActiveWaitlisted, we don't need to show the Code of Conduct modal, as they have already been presented with the LimitedMissionsModal component
      auth.user?.needsAcceptPlatformCodeOfConduct &&
      auth.basicAccess &&
      auth.userType === UserType.User
    ) {
      setIsCodeOfConductModalOpen();
    }
  }, [
    auth.user?.needsAcceptPlatformCodeOfConduct,
    auth.basicAccess,
    auth.userType,
  ]);

  useEffect(() => {
    if (
      !auth.user?.needsAcceptPlatformCodeOfConduct &&
      auth.withWhatsNew &&
      auth.user?.needsWhatsNewModal &&
      auth.user?.activatedAt &&
      auth.basicAccess &&
      auth.verified &&
      auth.userType === UserType.User
    ) {
      setIsWhatsNewModalOpen();
    }
  }, [
    auth.user?.needsAcceptPlatformCodeOfConduct,
    auth.withWhatsNew,
    auth.user?.needsWhatsNewModal,
    auth.user?.activatedAt,
    auth.basicAccess,
    auth.userType,
  ]);

  /*useEffect(() => {
    if (
      !auth.user?.needsAcceptPlatformCodeOfConduct &&
      auth.withWhatsNewBanner &&
      auth.user?.needsWhatsNewBanner &&
      auth.user?.activatedAt &&
      auth.basicAccess &&
      auth.verified &&
      auth.userType === UserType.User
    ) {
      setIsReleaseBannerOpen(true);
    }
  }, [
    !auth.user?.needsAcceptPlatformCodeOfConduct,
    auth.withWhatsNewBanner,
    auth.user?.needsWhatsNewBanner,
    auth.user?.activatedAt,
    auth.basicAccess,
    auth.userType,
  ]);*/

  document.setInnerTitle(title || 'Loading...');

  const acceptToS = () => {
    setLoading(auth.acceptToS(auth).then(() => setTOSModal(false)));
  };

  const showAcceptNewTOS =
    CONTRACTS_V2_ENABLED &&
    auth.uid &&
    auth.currentUser &&
    auth.currentUser?.acceptedTOSVersion !== 'v2';

  return (
    <div className={cx(!hideNavbar && styles.root, className)}>
      {showAcceptNewTOS && (
        <AcceptNewTOS tosAlreadyAccepted={!auth.currentUser?.needsAcceptTOS} />
      )}
      {auth.user?.emailNotificationsBlocked ? (
        <StickyStrip
          styles={{
            background: Colors.danger,
            color: Colors.light,
            padding: Paddings.small,
          }}
        >
          <div style={{ flex: 1, textAlign: 'center' }}>
            <Icon
              type={IconType.CloseWhiteCircle}
              style={{ marginRight: Spacing.small }}
            />
            Your email is on our suppression list due to delivery failures.
            Update your email to restore messaging. Contact{' '}
            <a
              style={{ color: Colors.light, fontWeight: FontWeights.bold }}
              href="mailto:help@a.team"
            >
              help@a.team
            </a>
          </div>
        </StickyStrip>
      ) : null}
      {!auth.isActiveWaitlistedAdVerified && isCodeOfConductModalOpen && (
        <CodeOfConductModal
          showOnly="platform"
          hideExit
          isOpen={isCodeOfConductModalOpen}
          toggleModal={setIsCodeOfConductModalOpen}
        />
      )}
      {auth.isReSubscriptionReminderEnabledForMissionNotifications ? (
        <ReSubscribeMissionNotificationsModal
          isOpen
          onClose={() => auth.unsetReSubscriptionReminderActiveForCategory()}
        />
      ) : null}
      <ReleaseTeamUpModal
        setIsReleaseStepsModalOpen={setIsReleaseStepsModalOpen}
        shouldSetWhatsNewModalAsDisplayed
        isOpen={!isCodeOfConductModalOpen && isWhatsNewModalOpen}
        onClose={setIsWhatsNewModalOpen}
      />
      <ReleaseStepsModal
        open={isReleaseStepsModalOpen}
        onClose={setIsReleaseStepsModalOpen}
        setIsWhatsNewModalOpen={setIsWhatsNewModalOpen}
      />
      {showTokenModal && (
        <TokenIntroductionModal
          show={rewards.showTokenIntroModal}
          onDismiss={rewards.dismissTokenIntroModal.bind(rewards)}
        />
      )}
      {auth.userType === UserType.CompanyUser ? (
        <ClientToSModal
          clientName={auth.user?.fullName || ''}
          onClose={() => {
            return;
          }}
          open={TOSModal && !hideTOSModal}
          onAccept={acceptToS}
        />
      ) : (
        <MakerToSModal
          open={TOSModal && !hideTOSModal}
          onClose={() => {
            return;
          }}
          onAccept={acceptToS}
        />
      )}
      {isMobile && !auth.user?.emailNotificationsBlocked && (
        <LinkedinBanner className={styles.linkedinBannerMobile} />
      )}
      {!hideNavbar && (
        <div ref={menuRef}>
          <MobileTopBar setMenuOpen={auth.user ? setMenuOpen : undefined} />
          <Navbar
            currentUser={auth.user}
            open={menuOpen}
            toggleMenu={setMenuOpen}
          />
        </div>
      )}

      <NotificationCenter
        isOpen={uiStore.notificationSlider}
        toggle={uiStore.toggleNotificationSlider}
      />

      {auth.withWhatsNewBanner && isReleaseBannerOpen && (
        <ReleaseConnectionsBanner
          onClose={() => setIsReleaseBannerOpen(false)}
        />
      )}

      {hero}

      {!isMobile && !auth.user?.emailNotificationsBlocked && (
        <LinkedinBanner className={styles.linkedinBannerDesktop} />
      )}

      <div className={cx(styles.container, containerClassName)}>
        {sidebar}
        <div className={cx(styles.children, { naked }, childrenClassName)}>
          {topNav && (
            <div className={cx([{ [styles.topNavWrapper]: !!isTopNavSticky }])}>
              {topNav}
            </div>
          )}
          {naked ? (
            children
          ) : (
            <div
              className={cx(styles.content, contentClassName)}
              style={{ ...style }}
            >
              {children}
            </div>
          )}
        </div>
      </div>
      <div className={cx(styles.termsFooter, { naked })}>
        &copy;{new Date().getFullYear()} ATeams Inc., All rights reserved.&nbsp;
        <br />
        <span className="link">
          <Link to={TermsOfServiceLocation}>Terms of Service</Link> |{' '}
          <Link to={PrivacyPolicyLocation}>Privacy Policy</Link>
        </span>
      </div>
      <LoadingIndicator loading={loading} />
    </div>
  );
}

export default observer(MainLayout);
